import { API_BASE_URL } from "../../config";
// src/Components/MapTrack.js
import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import StoreMarkers from "./Components/StoreMarkers";
import VehicleMarkers from "./Components/VehicleMarkers";
import useFetchUserSeller from "../Utils/useFetchUserSeller";  // Import the new hook
import { assignCustomerToRoute } from "../Utils/assignCustomerRoute";
import { useFetchData } from "../Utils/useFetchData";
import { fetchPurchaseRanking, trailing7Range, buildPurchaseMap, fetchRutineAll, buildRouteMap } from "../Utils/customerListData";
import Select from "react-select";

const MapEvents = ({ mapCenter, setMapCenter, zoom, setZoom, centerAtCar, setZoomedIn }) => {
    const map = useMap();

    const prevMapCenter = React.useRef(mapCenter);
    const prevZoom = React.useRef(zoom);

    // Programmatically move the map ONLY when mapCenter or zoom change from OUTSIDE MapEvents
    useEffect(() => {
        const isCenterChanged = !prevMapCenter.current || 
            Math.abs(prevMapCenter.current[0] - mapCenter[0]) > 0.000001 || 
            Math.abs(prevMapCenter.current[1] - mapCenter[1]) > 0.000001;
        const isZoomChanged = prevZoom.current !== zoom;

        if (isCenterChanged || isZoomChanged) {
            map.setView(mapCenter, zoom);
            prevMapCenter.current = mapCenter;
            prevZoom.current = zoom;
        }
    }, [mapCenter, zoom, map]);

    useEffect(() => {
        const handleMove = () => {
            if (centerAtCar) return; // Don't sync state back if we are following the car
            const center = map.getCenter();
            const newCenter = [center.lat, center.lng];
            
            // Update refs to match current map state so the effect above doesn't trigger setView
            prevMapCenter.current = newCenter;
            setMapCenter(newCenter);
        };

        const handleZoom = () => {
            const currentZoom = map.getZoom();
            
            // Update refs to match current map state
            prevZoom.current = currentZoom;
            setZoom(currentZoom);
            setZoomedIn(currentZoom > 17);
        };

        map.on("moveend", handleMove);
        map.on("zoomend", handleZoom);

        return () => {
            map.off("moveend", handleMove);
            map.off("zoomend", handleZoom);
        };
    }, [map, centerAtCar, setMapCenter, setZoom, setZoomedIn]);

    return null;
};

const POLL_INTERVAL_MS = 5000;
const POLL_STALE_MS = 15000;

const DashMap = ({ handleLogout }) => {
    const pollStartedAtRef = useRef(0);
    const defaultPosition = [15.325319, 101.970879];
    const defaultZoom = 19;

    const getSavedMapCenter = () => {
        try {
            const raw = localStorage.getItem("mapCenter");
            if (!raw) return defaultPosition;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length === 2) {
                const lat = Number(parsed[0]);
                const lng = Number(parsed[1]);
                if (isFinite(lat) && isFinite(lng)) return [lat, lng];
            }
            if (parsed && typeof parsed === "object" && "lat" in parsed && "lng" in parsed) {
                const lat = Number(parsed.lat);
                const lng = Number(parsed.lng);
                if (isFinite(lat) && isFinite(lng)) return [lat, lng];
            }
        } catch (e) {
            // ignore and fall back
        }
        return defaultPosition;
    };

    const getSavedZoom = () => {
        try {
            const raw = localStorage.getItem("mapZoom");
            if (!raw) return defaultZoom;
            const parsed = JSON.parse(raw);
            const z = Number(parsed);
            return isFinite(z) ? z : defaultZoom;
        } catch (e) {
            return defaultZoom;
        }
    };

    const savedMapCenter = getSavedMapCenter();
    const savedZoom = getSavedZoom();
    const [error, setError] = useState("");

    const [mapCenter, setMapCenter] = useState(savedMapCenter);
    const [zoom, setZoom] = useState(savedZoom);
    const [selectedUserId, setSelectedUserId] = useState(""); // State to track selected user ID
    const [centerAtCar, setCenterAtCar] = useState(false); // New state for centering map at car
    const [storeLocations, setStoreLocations] = useState([]);
    const [vehicleLocations, setVehicleLocations] = useState([]);
    const [markerPositions, setMarkerPositions] = useState({});
    const [originalPositions, setOriginalPositions] = useState({});
    const [zoomedIn, setZoomedIn] = useState(savedZoom >= 17);
    const [vehicletrackingData, setVehicleTrackingData] = useState(null); // State to store tracking data
    const [showCars, setShowCars] = useState(true);
    const [showStores, setShowStores] = useState(true);
    const [showRegular, setShowRegular] = useState(true);
    const [showWalkIn, setShowWalkIn] = useState(false);
    const [showPotential, setShowPotential] = useState(false);
    const [showAgent, setShowAgent] = useState(false);
    const [showUnavailable, setShowUnavailable] = useState(false);

    const { fetchData, checkToken } = useFetchData(handleLogout);

    // ซื้อสินค้าเฉลี่ย / สายที่รับผิดชอบ for the popup — loaded once, in parallel and NOT awaited by
    // the marker fetch, so the map renders as fast as before and the popup fills in when ready.
    // Each is { status: 'loading' | 'ready' | 'error', map } so the popup can say กำลังโหลด... /
    // โหลดไม่สำเร็จ instead of a false "ไม่มีข้อมูล" while the numbers are still on their way.
    const [purchase, setPurchase] = useState({ status: 'loading', map: {} });
    const [route, setRoute] = useState({ status: 'loading', map: {} });
    useEffect(() => {
        const token = checkToken();
        if (!token) return;
        const [start, end] = trailing7Range();
        fetchPurchaseRanking(token, start, end).then((r) =>
            setPurchase(r ? { status: 'ready', map: buildPurchaseMap(r) } : { status: 'error', map: {} }));
        fetchRutineAll(token).then((rows) => {
            if (!Array.isArray(rows)) return { status: 'error', map: {} };
            return buildRouteMap(token, rows).then((map) => ({ status: 'ready', map }));
        }).then(setRoute);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Use the custom hook for fetching user IDs
    const userIds = useFetchUserSeller(handleLogout);

    // New: Fetch customers by category (ALL) with optional seller filter
    const fetchCustomersByCategory = async (sellerId = undefined) => {
        try {
            const payload = sellerId ? { type: 'ALL', sellerId } : { type: 'ALL' };
            const resp = await fetchData(`${API_BASE_URL}/customer/api/customers/by-category`, "POST", payload);
            if (!resp) return;

            // resp structure example: { regular: [], walkIn: [], agent: [], potential: [] }
            const regular = Array.isArray(resp.regular) ? resp.regular : [];
            const walkIn = Array.isArray(resp.walkIn) ? resp.walkIn : [];
            const potential = Array.isArray(resp.potential) ? resp.potential : [];
            // Agent customers intentionally ignored per requirement
            const agent = Array.isArray(resp.agent) ? resp.agent : [];

            // Normalize to a unified list expected by StoreMarkers
            const normalize = []
                .concat(
                    regular.map((c) => ({
                        id: c.customerUid || c.id || `REG-${Math.random()}`,
                        customerUid: c.customerUid,
                        uid: c.customerUid, // Added for WaypointMarkers compatibility
                        storeName: c.storeName || c.customerName || "ร้านค้าทั่วไป",
                        latitude: c.latitude,
                        longitude: c.longitude,
                        purchaseToday: c.purchaseToday,
                        customerType: 'REGULAR',
                        type: 'REGULAR', // Added for EditCustomerData compatibility
                        customerAvailable: c.operating === undefined ? true : !!c.operating, // Match CustomerDataManagement
                        waypointId: c.waypointId || c.customerUid, // Added for WaypointMarkers display
                    })),
                    walkIn.map((w) => ({
                        id: w.walkInCustomerUid || w.id || `WAC-${Math.random()}`,
                        customerUid: w.walkInCustomerUid,
                        uid: w.walkInCustomerUid, // Added for WaypointMarkers compatibility
                        storeName: w.storeName || w.walkInCustomerName || "Walk-in",
                        latitude: w.latitude,
                        longitude: w.longitude,
                        purchaseToday: w.purchaseToday,
                        customerType: 'WALK_IN',
                        type: 'WALK_IN', // Added for EditCustomerData compatibility
                        customerAvailable: w.operating === undefined ? true : !!w.operating, // Match CustomerDataManagement
                        waypointId: w.waypointId || w.walkInCustomerUid, // Added for WaypointMarkers display
                    })),
                    agent.map((a) => ({
                        id: a.agentCustomerUid || a.id || `AGN-${Math.random()}`,
                        customerUid: a.agentCustomerUid,
                        uid: a.agentCustomerUid, // Added for WaypointMarkers compatibility
                        storeName: a.storeName || a.agentName || "ตัวแทน",
                        latitude: a.latitude,
                        longitude: a.longitude,
                        purchaseToday: a.purchaseToday,
                        customerType: 'AGENT',
                        type: 'AGENT', // Added for EditCustomerData compatibility
                        customerAvailable: a.operating === undefined ? true : !!a.operating, // Match CustomerDataManagement
                        waypointId: a.waypointId || a.agentCustomerUid, // Added for WaypointMarkers display
                    })),
                    potential.map((p) => ({
                        id: p.potentialCustomerUid || p.id || `PTC-${Math.random()}`,
                        customerUid: p.potentialCustomerUid,
                        uid: p.potentialCustomerUid, // Added for WaypointMarkers compatibility
                        storeName: p.storeName || "ว่าที่ร้านค้า",
                        latitude: p.latitude,
                        longitude: p.longitude,
                        purchaseToday: p.purchaseToday,
                        customerType: 'POTENTIAL',
                        type: 'POTENTIAL', // Added for EditCustomerData compatibility
                        customerAvailable: p.operating === undefined ? true : !!p.operating, // Match CustomerDataManagement
                        waypointId: p.waypointId || p.potentialCustomerUid, // Added for WaypointMarkers display
                    }))
                )
                // Filter out invalid coordinates
                .filter((s) => isFinite(s.latitude) && isFinite(s.longitude));

            setStoreLocations(normalize);
            const initialPositions = {};
            normalize.forEach((store) => {
                const uid = store.customerUid || store.id;
                initialPositions[uid] = [store.latitude, store.longitude];
            });
            setOriginalPositions(initialPositions);
        } catch (e) {
            console.error("Error fetching customers by category", e);
            setError("Error fetching customers by category");
        }
    };

    const fetchVehicleData = async () => {
        try {
            const data = await fetchData(`${API_BASE_URL}/gps/api/v1/gps/users/latest`);
            if (Array.isArray(data)) {
                const vehicles = data
                    .map((item) => {
                        const lat = Number(item.latitude);
                        const lng = Number(item.longitude);
                        if (!isFinite(lat) || !isFinite(lng)) return null;
                        return {
                            id: item.id ?? `${item.gpsUid || item.userUid || Math.random()}`,
                            latitude: lat,
                            longitude: lng,
                            heading: Number(item.direction) || 0,
                            speed: Number(item.speed) || 0,
                            battery: Number(item.battery),
                            charge: Boolean(item.charging),
                            signal: item.signal,
                            date: item.date,
                            time: item.time,
                            nameuser: item.nameuser || "GPS",
                            username: item.username || item.userUid || "aaa"
                        };
                    })
                    .filter(Boolean);
                setVehicleLocations(vehicles);
                
                // If centerAtCar is on and we have vehicles, center on the first one
                if (centerAtCar && vehicles.length > 0) {
                    setMapCenter([vehicles[0].latitude, vehicles[0].longitude]);
                }
            }
        } catch (error) {
            console.error("Error fetching vehicle data:", error);
            setError("Error fetching vehicle data.");
        }
    };

    const fetchAllUsersTrackingData = async () => {
        console.log("Fetching all users GPS data...");
        await fetchVehicleData();
    };

    const fetchTrackingData = async (userId) => {
        // Feature disabled: /api/Manager/ is no longer usable
        return;
    };


    const fetchVehicleTrackingData = async (userId) => {
        if (!userId) return; // If no user is selected, don't make the API call
        try {
            console.log(`Fetching GPS data for user: ${userId}`);
            const data = await fetchData(`${API_BASE_URL}/gps/api/v1/gps/user/${userId}/latest`);
            console.log("GPS data received:", data);
            if (data) {
                const lat = Number(data.latitude);
                const lng = Number(data.longitude);
                const vehicle = {
                    id: data.id ?? `${data.gpsUid || data.userUid || Math.random()}`,
                    latitude: lat,
                    longitude: lng,
                    heading: Number(data.direction) || 0,
                    speed: Number(data.speed) || 0,
                    battery: Number(data.battery),
                    charge: Boolean(data.charging),
                    signal: data.signal,
                    date: data.date,
                    time: data.time,
                    nameuser: data.nameuser || "GPS",
                    username: data.username || data.userUid || "aaa"
                };
                setVehicleTrackingData([vehicle]); // Set the tracking data in state as an array

                // If centerAtCar is on, center the map on the vehicle's new position
                if (centerAtCar) {
                    setMapCenter([lat, lng]);
                }
            }
        } catch (error) {
            console.error("Error fetching tracking data:", error);
            setError("Error fetching tracking data.");
        }
    };

    const handleManualRefresh = () => {
        if (selectedUserId) {
            fetchVehicleTrackingData(selectedUserId);
            fetchCustomersByCategory(selectedUserId);
        } else {
            fetchAllUsersTrackingData();
            fetchCustomersByCategory();
        }
    };


    const handleDeleteStoreLocation = async (storeId) => {
        // Feature disabled: /api/Manager/ is no longer usable
        return;
        /*
        try {
            const response = await fetchData(
                `${API_BASE_URL}/api/Manager/saleLocations/${storeId}`,
                "DELETE"
            );

            if (response) {
                // Successfully remove the deleted store from state
                setStoreLocations((prevLocations) =>
                    prevLocations.filter((location) => location.id !== storeId)
                );
            }
        } catch (error) {
            console.error("Delete error:", error);
            setError("Error deleting store location");
        }

        fetchCustomersByCategory(selectedUserId || undefined);
        */
    };



    const assignCustomerData = async (userId, storeId) => {
        try {
            const token = checkToken();
            if (!token) return;
            await assignCustomerToRoute(token, storeId, userId);
        } catch (error) {
            console.error("Assignment error:", error);
            setError("Error assigning customer data");
            alert("ตั้งสายที่รับผิดชอบไม่สำเร็จ: " + error.message);
        }

        // Optionally, fetch updated data to refresh UI
        fetchCustomersByCategory(selectedUserId || undefined);
    };


    const handleMarkerDragEnd = (event, id) => {
        const { lat, lng } = event.target.getLatLng();
        setMarkerPositions((prevPositions) => ({
            ...prevPositions,
            [id]: {
                oldPosition: originalPositions[id],
                newPosition: [lat, lng],
            },
        }));
    };

    const handleResetStorePosition = (id) => {
        if (originalPositions[id]) {
            setMarkerPositions((prevPositions) => {
                const updatedPositions = { ...prevPositions };
                delete updatedPositions[id];
                return updatedPositions;
            });
        }
    };

    useEffect(() => {
        localStorage.setItem("mapCenter", JSON.stringify(mapCenter));
        localStorage.setItem("mapZoom", JSON.stringify(zoom));
    }, [mapCenter, zoom]);

    useEffect(() => {
        if (selectedUserId) {
            // Initial load for a specific user (unlikely on first mount)
            fetchTrackingData(selectedUserId);
            fetchVehicleTrackingData(selectedUserId);
            fetchCustomersByCategory(selectedUserId);
        } else {
            // Initial load for all users via new combined API
            fetchAllUsersTrackingData();
            fetchCustomersByCategory();
        }
    }, []);

    useEffect(() => {
        const intervalId = setInterval(async () => {
            // ข้ามรอบ: แท็บไม่แสดง, หรือรอบก่อนยังไม่ตอบ (กันยิงซ้อนทับกัน — ถ้าค้างเกิน 15 วิให้เริ่มรอบใหม่)
            const now = Date.now();
            if (document.hidden || (pollStartedAtRef.current && now - pollStartedAtRef.current < POLL_STALE_MS)) {
                return;
            }
            pollStartedAtRef.current = now;
            try {
                if (selectedUserId) {
                    // Poll specific user data
                    await Promise.all([
                        fetchTrackingData(selectedUserId),
                        fetchVehicleTrackingData(selectedUserId),
                        fetchCustomersByCategory(selectedUserId),
                    ]);
                } else {
                    // Poll all users aggregated data
                    await Promise.all([fetchAllUsersTrackingData(), fetchCustomersByCategory()]);
                }
            } finally {
                pollStartedAtRef.current = 0;
            }
        }, POLL_INTERVAL_MS);

        // Clean up the interval when the component unmounts or dependencies change
        return () => clearInterval(intervalId);
    }, [selectedUserId]);

    useEffect(() => {
        if (selectedUserId) {
            fetchVehicleTrackingData(selectedUserId);  // Fetch tracking data when the selected user changes
            fetchCustomersByCategory(selectedUserId); // Fetch stores for selected seller
        } else {
            setVehicleTrackingData(null); // Do not make the API call and reset tracking data when "All Users" is selected
            fetchAllUsersTrackingData(); // Fetch aggregated data when switching back to All Users
            fetchCustomersByCategory(); // Fetch all customers
        }
    }, [selectedUserId]);


    const handleUserSelect = (e) => {
        const userId = e.target.value;
        setSelectedUserId(userId);
    };

    // Helpers to categorize and filter customers
    const getCustomerCategory = (store) => {
        const typeField = store?.customerType || store?.type || store?.category;
        const uid = store?.customerUid || store?.customerCode || store?.walkInCustomerUid || store?.potentialCustomerUid || "";
        const hasPotentialKey = !!store?.potentialCustomerUid || typeField === 'POTENTIAL' || store?.isPotential;
        if (typeField === 'AGENT' || store?.customerClass === 'AGENT') return 'AGENT';
        if (String(uid).startsWith('WAC-') || typeField === 'WALK_IN') return 'WALK_IN';
        if (String(uid).startsWith('REC-') || typeField === 'REGULAR') return 'REGULAR';
        if (hasPotentialKey) return 'POTENTIAL';
        // Fallback: if no uid but has lat/lng and storeName, consider potential
        if (!uid && store?.storeName && (store?.latitude || store?.longitude)) return 'POTENTIAL';
        return 'REGULAR';
    };

    const filterStoresByCategory = (stores) => {
        if (!Array.isArray(stores)) return [];
        return stores.filter((s) => {
            const cat = getCustomerCategory(s);
            if (cat === 'AGENT' && !showAgent) return false;
            if (cat === 'REGULAR' && !showRegular) return false;
            if (cat === 'WALK_IN' && !showWalkIn) return false;
            if (cat === 'POTENTIAL' && !showPotential) return false;
            if (!showUnavailable && s.customerAvailable === false) return false;
            return true;
        });
    };

    // Merge the shared per-customer numbers (same loaders as the ร้านค้า list) into the markers.
    const filteredStores = React.useMemo(
        () => filterStoresByCategory(storeLocations || []).map((s) => {
            const uid = s.customerUid || s.id;
            return {
                ...s,
                avgBagsPerWeek: purchase.map[uid] ? purchase.map[uid].bagsPerWeek : null,
                avgStatus: purchase.status,
                assignedSellers: route.map[uid] || [],
                routeStatus: route.status,
            };
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [storeLocations, purchase, route, showRegular, showWalkIn, showPotential, showAgent, showUnavailable]
    );

    return (
        <div>
            <div
                style={{marginBottom: "10px", display: "flex", alignItems: "center", gap: "20px", paddingLeft: "20px"}}>
                {/* User Selection Dropdown */}
                <div style={{flex: 1, maxWidth: "20%"}}>
                    <label htmlFor="user-select">สายที่แสดงข้อมูล:</label>
                    <Select
                        id="user-select"
                        options={[
                            {value: "", label: "แสดงทุกสาย"}, // Default option for all users
                            ...userIds.map((user) => ({
                                value: user.uid,
                                label: `${user.nameuser} (${user.username})`, // Format label to include username and nameuser
                            })),
                        ]}
                        value={
                            selectedUserId
                                ? {
                                    value: selectedUserId,
                                    label: userIds.find((user) => user.uid === selectedUserId)?.nameuser + " (" +
                                        userIds.find((user) => user.uid === selectedUserId)?.username + ")",
                                }
                                : {value: "", label: "แสดงทุกสาย"}
                        }
                        onChange={(selectedOption) => {
                            // Update selected user ID and reset tracking data if "All Users" is selected
                            const userId = selectedOption ? selectedOption.value : "";
                            setSelectedUserId(userId);
                        }}
                        formatOptionLabel={(option) => (
                            <div>
                                <span style={{fontWeight: "bold"}}>{option.label}</span>
                            </div>
                        )}
                        className="btc-selectBox-container"
                        classNamePrefix="btc-selectBox"
                        isClearable // Allows clearing of the selected option
                    />
                </div>

                {/* Checkboxes for Show Cars and Show Stores */}
                <div style={{display: "flex", gap: "20px", alignItems: "center"}}>
                    <label>
                        <input
                            type="checkbox"
                            checked={showCars}
                            onChange={(e) => setShowCars(e.target.checked)}
                        />
                        แสดงรถ
                    </label>
                    <label>
                        <input
                            type="checkbox"
                            checked={showStores}
                            onChange={(e) => setShowStores(e.target.checked)}
                        />
                        แสดงร้านค้า
                    </label>
                    {showStores && (
                        <div style={{display: 'flex', gap: '10px', marginLeft: '10px'}}>
                            <label>
                                <input type="checkbox" checked={showRegular} onChange={(e)=>setShowRegular(e.target.checked)} /> ปกติ
                            </label>
                            <label>
                                <input type="checkbox" checked={showWalkIn} onChange={(e)=>setShowWalkIn(e.target.checked)} /> Walk-in
                            </label>
                            <label>
                                <input type="checkbox" checked={showPotential} onChange={(e)=>setShowPotential(e.target.checked)} /> ว่าที่ร้านค้า
                            </label>
                            <label>
                                <input type="checkbox" checked={showAgent} onChange={(e)=>setShowAgent(e.target.checked)} /> ตัวแทน
                            </label>
                            <label>
                                <input type="checkbox" checked={showUnavailable} onChange={(e)=>setShowUnavailable(e.target.checked)} /> รวมร้านที่ไม่แสดงกับคนขาย
                            </label>
                        </div>
                    )}
                    <label style={{ display: "flex", alignItems: "center", gap: "5px", cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={centerAtCar}
                            onChange={(e) => setCenterAtCar(e.target.checked)}
                        />
                        ล็อคกึ่งกลางที่รถ
                    </label>
                    <button
                        onClick={handleManualRefresh}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                        }}
                    >
                        <i className="fas fa-sync-alt"></i>
                        รีเฟรช GPS
                    </button>
                </div>
            </div>
            <MapContainer center={mapCenter} key="dashmap" zoom={zoom}
                          style={{height: "800px", width: "100%", zIndex: "15"}}>
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <MapEvents
                    mapCenter={mapCenter}
                    setMapCenter={setMapCenter}
                    zoom={zoom}
                    setZoom={setZoom}
                    centerAtCar={centerAtCar}
                    setZoomedIn={setZoomedIn}
                />
                {/* Display store and vehicle locations */}
                {showStores && (
                    <StoreMarkers
                        storeLocations={filteredStores} // Filtered by type selections
                        markerPositions={markerPositions}
                        zoomedIn={zoomedIn}
                        handleMarkerDragEnd={handleMarkerDragEnd}
                        handleResetStorePosition={handleResetStorePosition}
                        handleDeleteStoreLocation={handleDeleteStoreLocation}
                        userIds={userIds}
                        assignCustomerData={assignCustomerData}
                    />
                )}
                {showCars && (
                    <VehicleMarkers 
                        vehicleLocations={selectedUserId ? vehicletrackingData : vehicleLocations} 
                        zoomedIn={zoomedIn} 
                        tracking={false}
                    />
                )}
            </MapContainer>
        </div>
    );
};

export default DashMap;
